/* eslint-disable  import/no-unresolved */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { withFeature } from 'flagged';
import {
  Container,
  Row,
  Col,
  Button,
  Title,
  Text,
  Alert,
} from '@cochlear-design-system/foundation';
import { QRCodeCanvas } from 'qrcode.react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import providerService from '../../services/providerService';
import { routes, onboardingURL } from '../../config';
import headerCancelModal from '../../actions/headerCancelModalAction';
import { getFindClinicModalConfig } from '../../cdsConfigs/findClinicModalConfig';
import { getLabelsForFields } from '../../utils/labelHelper';
import { FindClinicModal } from '../../ui/FindClinicModal/FindClinicModal';
import { getMergedAddress } from '../../utils/addressHelper';

const OnboardingQRCode = ({ rendering, country }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userCountry = country.toUpperCase();
  const modalTrigger = useSelector(
    (payload) => payload.headerCancelModal,
  );
  const { name: clinicName } = providerService.getCurrentProvider();
  const { hasClinic, providerId } =
    providerService.getProviderDetails();
  const openPathPrefix = `${onboardingURL}clinicId=${providerId}`;
  const [openPath, setOpenPath] = useState(openPathPrefix);
  const [
    showSelectSurgeryCentreModal,
    setShowSelectSurgeryCentreModal,
  ] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [clinicData, setClinicData] = useState([]);
  const labels = getLabelsForFields(rendering.fields);

  const handleSearchClinic = async (searchParam) => {
    const countryCode = searchParam.country || userCountry;
    const clinics = await providerService.searchClinics(
      countryCode,
      searchParam.clinic,
    );
    /* eslint no-param-reassign: ["error", { "props": false }] */
    if (clinics.providers && clinics.providers.length > 0) {
      clinics.providers.forEach((provider) => {
        provider.address = getMergedAddress(provider);
      });
    }
    return clinics.providers;
  };

  const handleSelectClinic = (action, params) => {
    const selectedClinicData = [];
    selectedClinicData.push({
      clinicId: params.data.id,
      name: params.data.name,
      address: params.data.address,
      isRemovable: true,
    });
    setClinicData(selectedClinicData);
  };

  const clinicSelectorConfig = getFindClinicModalConfig(
    userCountry,
    labels,
  );
  clinicSelectorConfig.searchButton.onClick = handleSearchClinic;
  clinicSelectorConfig.tableConfig.events.onRowClick =
    handleSelectClinic;

  const getQRCode = () => {
    return (
      <QRCodeCanvas
        id="qrCode"
        value={openPath}
        size={250}
        bgColor="#ffffff"
        level="H"
      />
    );
  };

  const redirectToHome = () => {
    history.push(routes.dpx.home);
  };

  const getSurgeryCentreName = () => {
    if (clinicData.length) {
      const [first] = clinicData;
      return first.name;
    }
    return 'N/A';
  };

  const getSurgeryCentreActionType = () => {
    if (clinicData.length) {
      return labels['labels.onboardingQRCode.update'];
    }
    return labels['labels.onboardingQRCode.add'];
  };

  useEffect(() => {
    if (modalTrigger) {
      dispatch(headerCancelModal(false));
      redirectToHome();
    }
  }, [modalTrigger]);

  useEffect(() => {
    if (clinicData.length > 0) {
      const [first] = clinicData;
      const path = `${openPathPrefix}&surgeryid=${first.clinicId}`;
      setOpenPath(path);
    }
  }, [clinicData]);
  useEffect(() => {
    const timerId = setTimeout(() => {
      setIsCopied(false);
    }, 55000);
    return () => {
      clearTimeout(timerId);
    };
  }, [isCopied]);

  const openInNewTab = () => {
    window.open(openPath, '_blank', 'noreferrer');
  };

  return (
    <>
      <Container className="ccl__qr-code-container">
        <Row>
          <Col>
            <Title
              content={labels['labels.onboardingQRCode.title']}
              size="heading-1"
              tag="h1"
              className="qr-code-page-title"
            />
            <Title
              content={labels['labels.onboardingQRCode.scanText']}
              size="heading-1"
              tag="h1"
              className="qr-code-page-title-print"
            />
          </Col>
        </Row>
        <div className="qr-code-wrapper">
          <Row>
            <Col>
              <Title
                content={
                  labels['labels.onboardingQRCode.createAccount']
                }
                size="heading-2"
                tag="h2"
                className="qr-code-title"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Text
                content={
                  labels['labels.onboardingQRCode.startAccount']
                }
                type="body-text-bold"
                className="qr-code-body-print"
              />
              <Text
                content={`${labels['labels.onboardingQRCode.clinic']} ${clinicName}`}
                type="body-text"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Text
                content={`${
                  labels['labels.onboardingQRCode.surgeryCenter']
                } ${getSurgeryCentreName()}`}
                type="body-text"
                className={`${
                  getSurgeryCentreName() === 'N/A'
                    ? 'qr-code-surgery-centre-na'
                    : ''
                } `}
              />
            </Col>
          </Row>
          {hasClinic && (
            <Row>
              <Col>
                <Text
                  content={`${getSurgeryCentreActionType()} ${
                    labels[
                      'labels.onboardingQRCode.surgeonCenterSuffix'
                    ]
                  }`}
                  type="link-text"
                  onClick={() => {
                    setShowSelectSurgeryCentreModal(true);
                  }}
                  className="qr-code-select-surgery-centre"
                  data-analytics="add_clinic_send_qrcode"
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <div className="qr-code">{getQRCode()}</div>
            </Col>
          </Row>
          <Row>
            <Col className="qr-code-print-btn">
              <Button
                text={labels['labels.onboardingQRCode.print']}
                onClick={() => window.print()}
                data-analytics="print_send_qrcode"
              />
            </Col>
            <Col className="qr-code-copy-link-btn">
              <CopyToClipboard text={openPath}>
                <Button
                  text={labels['labels.onboardingQRCode.copy']}
                  variant="secondary"
                  onClick={() => setIsCopied(true)}
                  data-analytics="copy_send_qrcode"
                />
              </CopyToClipboard>
            </Col>
          </Row>
          <Row>
            <Col className="qr-code-open-link-btn">
              <Button
                text={labels['labels.onboardingQRCode.openNewTab']}
                variant="tertiary"
                onClick={() => openInNewTab()}
                data-analytics="open_tab_send_qrcode"
              />
            </Col>
          </Row>
        </div>
        {isCopied && (
          <div className="qr-code-alert-wrapper">
            <Alert
              dismissible
              heading={labels['labels.onboardingQRCode.linkCopied']}
              variant="brand"
              onClose={() => setIsCopied(false)}
              data-analytics="close_copy_send_qrcode"
            />
          </div>
        )}
      </Container>
      <FindClinicModal
        {...clinicSelectorConfig}
        show={showSelectSurgeryCentreModal}
        onHide={() => setShowSelectSurgeryCentreModal(false)}
        labels={labels}
      />
    </>
  );
};

export default withFeature('featureEnabler/createPatient')(
  OnboardingQRCode,
);
